import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";

// Notifications (owner 2026-11-03: „activează notificările în contul admin și contul client”): the bell of the admin
// and of the restaurants' app. It asks for the list every 30 s (also while the tab is in the background); opening
// it marks everything as seen. With the browser's permission, a new notification also shows on the screen, while
// the page is open somewhere — nothing reaches a closed browser (no push service).

export interface NotificationItem {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: number;
  unread: boolean;
}
export interface NotificationList {
  items: NotificationItem[];
  unread: number;
}

const POLL = 30_000;

const when = (ms: number) => {
  const min = Math.round((Date.now() - ms) / 60_000);
  if (min < 1) return "acum";
  if (min < 60) return `acum ${min} min`;
  return new Date(ms).toLocaleString("ro-RO", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const canNotify = () => typeof window !== "undefined" && "Notification" in window;

function readMark(key: string): number | null {
  try {
    const v = localStorage.getItem(key);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}
function writeMark(key: string, v: number) {
  try {
    localStorage.setItem(key, String(v));
  } catch {
    // Private mode: the browser notifications just start over.
  }
}

export function NotificationBell({
  scope,
  load,
  markRead,
}: {
  /** "admin" or the restaurant id: separates the lists and what this browser already showed. */
  scope: string;
  load: () => Promise<NotificationList>;
  markRead: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ["notifications", scope];
  const q = useQuery({
    queryKey: key,
    queryFn: load,
    refetchInterval: POLL,
    refetchIntervalInBackground: true,
  });
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState(() => (canNotify() ? Notification.permission : "denied"));
  const wrap = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Browser notifications: only what arrived after this browser last looked (the first load shows nothing).
  const items = q.data?.items;
  useEffect(() => {
    if (!items) return;
    const markKey = `mnv-notified:${scope}`;
    const newest = items[0]?.createdAt ?? Date.now();
    const last = readMark(markKey);
    if (last === null) {
      writeMark(markKey, newest);
      return;
    }
    const fresh = items.filter((n) => n.createdAt > last && n.unread);
    if (!fresh.length) return;
    writeMark(markKey, Math.max(last, newest));
    if (!canNotify() || Notification.permission !== "granted") return;
    for (const n of fresh.slice(0, 3).reverse()) {
      try {
        const shown = new Notification(n.title, {
          body: n.body ?? undefined,
          tag: n.id,
          icon: "/brand/meniova-symbol.svg",
        });
        shown.onclick = () => {
          window.focus();
          if (n.link) navigate(n.link);
          shown.close();
        };
      } catch {
        // Some phones allow notifications only from a service worker: the bell still shows them.
      }
    }
  }, [items, scope, navigate]);

  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const unread = q.data?.unread ?? 0;

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await markRead().catch(() => undefined);
      // The list keeps its "nou" marks while open; the badge goes away.
      qc.setQueryData<NotificationList>(key, (d) => (d ? { ...d, unread: 0 } : d));
    } else if (!next) void qc.invalidateQueries({ queryKey: key });
  }

  async function allow() {
    if (!canNotify()) return;
    setPermission(await Notification.requestPermission());
  }

  return (
    <div className="pop" ref={wrap}>
      <button
        type="button"
        className="icon-btn notif-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={unread ? `Notificări: ${unread} noi` : "Notificări"}
        onClick={() => void toggle()}
      >
        <Bell size={20} strokeWidth={1.75} aria-hidden="true" />
        {unread > 0 && <span className="notif-badge">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="pop-menu notif-panel" role="dialog" id={panelId} aria-label="Notificări">
          <div className="pop-head">
            <span style={{ fontWeight: 600 }}>Notificări</span>
          </div>
          {canNotify() && permission === "default" && (
            <button
              type="button"
              className="btn btn-secondary btn-sm notif-allow"
              onClick={() => void allow()}
            >
              Arată notificările și pe ecran
            </button>
          )}
          {canNotify() && permission === "denied" && (
            <p className="caption muted notif-note">
              Notificările pe ecran sunt blocate în browser. Le poți permite din setările site-ului.
            </p>
          )}
          {q.isPending && <p className="caption muted notif-note">Se încarcă…</p>}
          {q.data && !q.data.items.length && (
            <p className="caption muted notif-note">Nicio notificare încă.</p>
          )}
          <ul className="notif-list">
            {q.data?.items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={`notif-item${n.unread ? " is-new" : ""}`}
                  onClick={() => {
                    setOpen(false);
                    void qc.invalidateQueries({ queryKey: key });
                    if (n.link) navigate(n.link);
                  }}
                >
                  <span className="notif-title">{n.title}</span>
                  {n.body && <span className="notif-body">{n.body}</span>}
                  <span className="notif-time">{when(n.createdAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
